import React, { useEffect, useRef, useState, useMemo } from 'react'
import { Route, Routes, useLocation } from 'react-router-dom'

import Header from '../../components/Header'
import HeaderInfo from '../../components/HeaderInfo'
import { PageTabs } from '../../components/PageTabs'
import Footer from '../../components/Footer'
import General from './General'

import '../../assets/css/Setting.css'
import Password from './Password'
import TeamMember from './TeamMember'
import DeleteTeam from '../../components/Settings/DeleteTeam'
import Notification from '../../components/Notification'
import AddTeam from '../../components/Settings/AddTeam'
import { adminTeamListApi, deleteAdminApi, getSingleAdminApi, updateSingleAdminApi } from '../../api/usersApi'

const Setting = () => {

    const location = useLocation();
    const userEditRef = useRef();
    const deleteUserRef = useRef();
    const addUserRef = useRef();
    const editRef = useRef();
    const debounceRef = useRef(null);
    const [menuActive, setMenuActive] = useState(false);
    const [activePage, setActivePage] = useState("General");
    const [showNotification, setShowNotification] = useState(false);
    const [notificationMessage, setNotificationMessage] = useState('');
    const [showDeleteTeamPopup, setShowDeleteTeamPopup] = useState(false);
    const [showAddTeamPopup, setShowAddTeamPopup] = useState(false);
    const [ProfileTabs, setProfileTabs] = useState([
        { name: `General`, href: '/setting/general' },
        { name: `Password`, href: '/setting/password' },
        { name: `Team Members`, href: '/setting/team' },
    ]);
    const [isLoading, setIsLoading] = useState(true)
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPage, setTotalPage] = useState(1);
    const [teamList, setTeamList] = useState([]);
    const [isLoadingData, setIsLoadingData] = useState(true);
    const [selectedAdmin, setSelectedAdmin] = useState({})
    const [name, setFirstName] = useState('')
    const [lastName, setLastName] = useState('')
    const [email, setEmail] = useState('')
    const [password, setPassword] = useState('')
    const [isEdit, setIsEdit] = useState(false);
    const [isSaveLoading, setIsSaveLoading] = useState(false)

    useEffect(() => {
        const path = location?.pathname?.toLowerCase();
        if (path.includes('/password')) setActivePage('Password');
        else if (path.includes('/team')) setActivePage('Team Members');
        else setActivePage('General');
    }, [location?.pathname]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (deleteUserRef.current && !deleteUserRef.current.contains(event.target)) {
                setShowDeleteTeamPopup(false);
            }
            if (addUserRef.current && !addUserRef.current.contains(event.target)) {
                setShowAddTeamPopup(false);
            }
            if (editRef.current && !editRef.current.contains(event.target)) {
                setIsEdit(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    useEffect(() => {
        setIsLoading(true);
        setIsLoadingData(true);
        if (debounceRef?.current) {
            clearTimeout(debounceRef.current);
        }
        debounceRef.current = setTimeout(() => {
            fetchTeam();
        }, 1500);

        return () => clearTimeout(debounceRef.current);
    }, [currentPage, location?.pathname]);

    const teamApi = async () => {
        const data = await adminTeamListApi({
            page: currentPage || 1,
            limit: 5,
            role: 2,
        });
        return data;
    }

    const fetchTeam = async () => {
        setIsLoading(false);
        try {
            setIsLoading(true);
            setIsLoadingData(true);
            const data = await teamApi();
            if (data?.list?.length <= 0) {
                setIsLoadingData(false)
            }
            setTeamList(data?.list);
            setTotalPage(data?.totalPage);
            setIsLoading(false);
        } catch (error) {
            console.log(error);
            setIsLoading(false);
            setIsLoadingData(false);
        }
    };

    const teamListMemo = useMemo(() => {
        return teamList;
    }, [teamList])

    const fetchData = async (selectedAdminData) => {
        try {
            const response = await getSingleAdminApi(selectedAdminData?.adminId)
            if (response) {
                setFirstName(response?.name)
                setLastName(response?.lastName)
                setEmail(response?.email)
            }
        } catch (error) {
            console.log(error);
        }
    }

    const updateData = async ({ changeName, changeLastName, changeEmail, changePassword, setErrorMessage }) => {
        setShowNotification(false);
        let success = false;
        setIsSaveLoading(false);
        try {
            setIsSaveLoading(true);
            success = await updateSingleAdminApi({
                adminId: selectedAdmin?.adminId,
                data: {
                    name: changeName,
                    lastName: changeLastName,
                    email: changeEmail,
                    password: changePassword,
                }
            });
            if (success) {
                const data = await teamApi();
                if (data) {
                    setTeamList(data?.list)
                    setShowNotification(true);
                    setNotificationMessage('Your changes have been successfully saved.');
                    setTimeout(() => {
                        setShowNotification(false);
                    }, 8000);
                    setIsEdit(false);
                    setIsSaveLoading(false)
                }
            }
        } catch (error) {
            console.log(error)
            const errMsg = error?.response?.data?.message;
            if (errMsg?.toLowerCase().includes("email")) {
                setErrorMessage((prev) => ({
                    ...prev,
                    alreadyUsedEmail: errMsg,
                }));
            }
            setIsSaveLoading(false)
        }
    }

    const handleDelete = async () => {
        setShowNotification(false);
        let success = false;
        setIsSaveLoading(false);
        try {
            setIsSaveLoading(true);
            success = await deleteAdminApi(selectedAdmin?.adminId);
            if (success) {
                const data = await teamApi();
                if (data) {
                    setTeamList(data?.list);
                    setTotalPage(data?.totalPage);
                    setShowNotification(true);
                    setNotificationMessage(`${selectedAdmin?.name} was deleted from the team.`);
                    setTimeout(() => {
                        setShowNotification(false);
                    }, 8000);
                    setIsEdit(false);
                    setIsSaveLoading(false)
                    setShowDeleteTeamPopup(false)
                    if (data?.list?.length === 0) {
                        setCurrentPage(currentPage > 1 ? currentPage - 1 : 1)
                    }
                }
            }
        } catch (error) {
            console.log(error)
            setIsSaveLoading(false)
        }
    }

    return (
        <div className='main-wrapper setting-page'>
            <Header activePage="setting" menuActive={menuActive} setMenuActive={setMenuActive} />
            <div className="main-content">
                <div className={`content-wrapper`}>
                    <HeaderInfo title='Settings' description='Manage your account settings, update your password, and organize your team members from one place.' />
                    <PageTabs tabs={ProfileTabs} activePage={activePage} setActivePage={setActivePage} />
                    <div className='setting-tab-decription'>
                        <div className="title">{activePage}</div>
                        <div className="description">{activePage === 'General' ? 'Update your name, email, and profile picture.' : activePage === 'Password' ? 'Update your password.' : 'Manage your team members.'}</div>
                    </div>
                    <div className="settingContent">
                        <Routes basename="/setting">
                            <Route path="/"
                                element={
                                    <General
                                        setShowNotification={setShowNotification}
                                        setNotificationMessage={setNotificationMessage}
                                        userEditRef={userEditRef}
                                    />
                                } >
                            </Route>
                            <Route path="/general"
                                element={
                                    <General
                                        setShowNotification={setShowNotification}
                                        setNotificationMessage={setNotificationMessage}
                                        userEditRef={userEditRef}
                                    />
                                } >
                            </Route>
                            <Route path="/password"
                                element={
                                    <Password
                                        setShowNotification={setShowNotification}
                                        setNotificationMessage={setNotificationMessage}
                                        userEditRef={userEditRef}
                                    />
                                } >

                            </Route>
                            <Route path="/team"
                                element={
                                    <TeamMember
                                        setShowNotification={setShowNotification}
                                        setNotificationMessage={setNotificationMessage}
                                        userEditRef={userEditRef}
                                        setShowDeleteTeamPopup={setShowDeleteTeamPopup}
                                        setShowAddTeamPopup={setShowAddTeamPopup}
                                        teamListMemo={teamListMemo}
                                        isLoadingData={isLoadingData}
                                        totalPage={totalPage}
                                        currentPage={currentPage}
                                        setCurrentPage={setCurrentPage}
                                        setIsLoading={setIsLoading}
                                        isLoading={isLoading}
                                        setIsLoadingData={setIsLoadingData}
                                        setSelectedAdmin={setSelectedAdmin}
                                        fetchData={fetchData}
                                        setIsEdit={setIsEdit}
                                        setFirstName={setFirstName}
                                        setLastName={setLastName}
                                        setEmail={setEmail}
                                        setPassword={setPassword}
                                    />
                                } >
                            </Route>
                        </Routes>
                    </div>
                </div>
                <Footer menuActive={menuActive} />
                {
                    showDeleteTeamPopup &&
                    <DeleteTeam
                        ref={deleteUserRef}
                        isOpen={showDeleteTeamPopup}
                        onClose={() => setShowDeleteTeamPopup(false)}
                        setShowDeleteTeamPopup={setShowDeleteTeamPopup}
                        title={`Are you sure you want to delete ${selectedAdmin?.name} ${selectedAdmin?.lastName}?`}
                        description={`Deleting ${selectedAdmin?.name} means they will be permanently deleted from using the admin dashboard.`}
                        setShowNotification={setShowNotification}
                        setNotificationMessage={setNotificationMessage}
                        handleDelete={handleDelete}
                        isSaveLoading={isSaveLoading}
                    />
                }
                {
                    showNotification &&
                    <Notification showNotification={showNotification} notificationMessage={notificationMessage} setShowNotification={setShowNotification} />
                }
                {
                    (showAddTeamPopup || isEdit) &&
                    <AddTeam
                        ref={isEdit ? editRef : addUserRef}
                        isOpen={true}
                        onClose={() => {
                            isEdit ? setIsEdit(false) : setShowAddTeamPopup(false)
                        }}
                        setShowAddTeamPopup={setShowAddTeamPopup}
                        title={isEdit ? 'Edit team member' : 'New team member'}
                        description="Deleting Bar means they will be permanently deleted from using the admin dashboard."
                        setShowNotification={setShowNotification}
                        setNotificationMessage={setNotificationMessage}
                        setIsSaveLoading={setIsSaveLoading}
                        isSaveLoading={isSaveLoading}
                        teamApi={teamApi}
                        setTeamList={setTeamList}
                        setFirstName={setFirstName}
                        name={name}
                        setLastName={setLastName}
                        lastName={lastName}
                        setEmail={setEmail}
                        email={email}
                        setPassword={setPassword}
                        password={password}
                        setTotalPage={setTotalPage}
                        isEdit={isEdit}
                        updateData={updateData}
                        selectedAdmin={selectedAdmin}
                        setIsEdit={setIsEdit}
                    />
                }
            </div>
        </div>
    )
}

export default Setting